import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Send, Mail, Users, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SendCampaign = () => {
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'group', 'individual', 'limit'
  const [selectedGroup, setSelectedGroup] = useState('');
  const [recipientLimit, setRecipientLimit] = useState('');
  const [excludePreviouslySent, setExcludePreviouslySent] = useState(false);
  const [previouslySentCount, setPreviouslySentCount] = useState(0);

  // Auto-select all when in 'all' mode and recipients are loaded
  useEffect(() => {
    if (selectionMode === 'all' && recipients.length > 0) {
      setSelectedRecipients(recipients.map(r => r.id));
    }
  }, [selectionMode, recipients]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, recipientsRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/recipients`)
      ]);
      setTemplates(templatesRes.data);
      setRecipients(recipientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  // Fetch previously sent recipients when template is selected
  useEffect(() => {
    if (selectedTemplate && excludePreviouslySent) {
      fetchPreviouslySent();
    }
  }, [selectedTemplate, excludePreviouslySent]);

  const fetchPreviouslySent = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.get(`${API}/history`);
      const sentToTemplate = response.data.filter(h => h.template_id === selectedTemplate);
      const sentEmails = new Set(sentToTemplate.map(h => h.recipient_email));
      setPreviouslySentCount(sentEmails.size);
    } catch (error) {
      console.error('Error fetching previously sent:', error);
    }
  };

  const getAvailableRecipients = () => {
    let available = recipients;

    // Filter by group if selected
    if (selectionMode === 'group' && selectedGroup) {
      available = available.filter(r => r.group === selectedGroup);
    }

    // Exclude previously sent
    if (excludePreviouslySent && selectedTemplate) {
      // We'll need to fetch history to exclude
      // For now, just return available
    }

    return available;
  };

  const handleLimitSelection = () => {
    const limit = parseInt(recipientLimit);
    if (isNaN(limit) || limit <= 0) {
      toast.error('Lütfen geçerli bir sayı girin');
      return;
    }

    const available = getAvailableRecipients();
    
    if (limit > available.length) {
      toast.warning(`Sadece ${available.length} alıcı mevcut. Tümü seçildi.`);
      setSelectedRecipients(available.map(r => r.id));
    } else {
      // Random selection
      const shuffled = [...available].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, limit);
      setSelectedRecipients(selected.map(r => r.id));
      toast.success(`${limit} alıcı rastgele seçildi`);
    }
  };

  const uniqueGroups = [...new Set(recipients.filter(r => r.group).map(r => r.group))];
  
  const filteredRecipients = selectionMode === 'group' && selectedGroup
    ? recipients.filter(r => r.group === selectedGroup)
    : recipients;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(filteredRecipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  const handleGroupSelection = (group) => {
    setSelectedGroup(group);
    const groupRecipients = recipients.filter(r => r.group === group);
    setSelectedRecipients(groupRecipients.map(r => r.id));
  };

  const handleRecipientToggle = (recipientId) => {
    setSelectedRecipients(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Lütfen bir şablon seçin');
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error('Lütfen en az bir alıcı seçin');
      return;
    }

    setLoading(true);
    try {
      let scheduleTimeISO = null;
      if (scheduleDate && scheduleTime) {
        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        scheduleTimeISO = scheduledDateTime.toISOString();
      }

      const response = await axios.post(`${API}/send-email`, {
        template_id: selectedTemplate,
        recipient_ids: selectedRecipients,
        schedule_time: scheduleTimeISO,
        exclude_previously_sent: excludePreviouslySent
      });

      if (scheduleTimeISO) {
        toast.success(`✅ Kampanya ${new Date(scheduleTimeISO).toLocaleString('tr-TR')} için zamanlandı!`);
      } else {
        toast.success(response.data.message || '✅ Kampanya gönderiliyor!');
      }
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kampanya gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedTemplate('');
    setSelectedRecipients([]);
    setScheduleDate('');
    setScheduleTime('');
    setStep(1);
  };

  if (step === 3) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="text-center space-y-6 max-w-md">
          <div className="mx-auto w-24 h-24 bg-[#10b981]/10 rounded-full flex items-center justify-center">
            <CheckCircle size={48} className="text-[#10b981]" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-[#0f172a] mb-2">Kampanya Gönderildi!</h2>
            <p className="text-lg text-[#64748b]">
              Kampanyanız işleniyor ve emailler kısa süre içinde teslim edilecek.
            </p>
          </div>
          <div className="bg-[#f8fafc] rounded-xl p-6 border border-[#e2e8f0]">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#64748b] mb-1">Alıcılar</p>
                <p className="text-2xl font-bold text-[#0f172a]">{selectedRecipients.length}</p>
              </div>
              <div>
                <p className="text-sm text-[#64748b] mb-1">Durum</p>
                <p className="text-sm font-medium text-[#10b981]">İşleniyor</p>
              </div>
            </div>
          </div>
          <Button
            onClick={resetForm}
            className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white"
          >
            Başka Kampanya Gönder
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="send-campaign-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight">Send Campaign</h1>
        <p className="text-lg text-[#64748b] mt-2">Create and send email campaigns to your recipients</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[1, 2].map((num) => (
          <React.Fragment key={num}>
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= num
                    ? 'bg-[#6366f1] text-white'
                    : 'bg-[#f8fafc] text-[#cbd5e1] border-2 border-[#e2e8f0]'
                }`}
              >
                {num}
              </div>
              <span className={`font-medium ${step >= num ? 'text-[#0f172a]' : 'text-[#cbd5e1]'}`}>
                {num === 1 ? 'Şablon Seç' : 'Alıcı Seç'}
              </span>
            </div>
            {num < 2 && <div className="flex-1 h-0.5 bg-[#e2e8f0]"></div>}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Select Template */}
          {step === 1 && (
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-8 space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail size={24} className="text-[#6366f1]" />
                <h2 className="text-2xl font-bold text-[#0f172a]">Email Şablonu Seçin</h2>
              </div>

              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <Mail size={48} className="mx-auto text-[#cbd5e1] mb-4" />
                  <p className="text-[#64748b]">Henüz şablon yok. Lütfen önce bir şablon oluşturun.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      data-testid={`template-option-${template.id}`}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTemplate === template.id
                          ? 'border-[#6366f1] bg-[#6366f1]/5'
                          : 'border-[#e2e8f0] hover:border-[#cbd5e1]'
                      }`}
                    >
                      <h3 className="font-bold text-[#0f172a] mb-1">{template.name}</h3>
                      <p className="text-sm text-[#64748b]">{template.subject}</p>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => setStep(2)}
                disabled={!selectedTemplate}
                data-testid="next-to-recipients-btn"
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white mt-6"
              >
                Sonraki: Alıcı Seç
              </Button>
            </div>
          )}

          {/* Step 2: Select Recipients */}
          {step === 2 && (
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-8 space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users size={24} className="text-[#6366f1]" />
                  <h2 className="text-2xl font-bold text-[#0f172a]">Alıcı Seçin</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(1)}
                >
                  Geri
                </Button>
              </div>

              {recipients.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-[#cbd5e1] mb-4" />
                  <p className="text-[#64748b]">Henüz alıcı yok. Lütfen önce alıcı ekleyin.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Selection Mode */}
                  <div className="grid grid-cols-4 gap-2 p-2 bg-[#f8fafc] rounded-lg">
                    <button
                      onClick={() => {
                        setSelectionMode('all');
                        setSelectedRecipients(recipients.map(r => r.id));
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'all'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Hepsine
                    </button>
                    <button
                      onClick={() => setSelectionMode('limit')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'limit'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Sayı Limiti
                    </button>
                    <button
                      onClick={() => setSelectionMode('group')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'group'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Grup
                    </button>
                    <button
                      onClick={() => {
                        setSelectionMode('individual');
                        setSelectedRecipients([]);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'individual'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Tekil
                    </button>
                  </div>

                  {/* Exclude Previously Sent Checkbox */}
                  <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="exclude-sent"
                        checked={excludePreviouslySent}
                        onCheckedChange={setExcludePreviouslySent}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor="exclude-sent"
                          className="font-semibold text-gray-800 cursor-pointer flex items-center gap-2"
                        >
                          🚫 Daha Önce Bu Kampanyayı Alanlara Gönderme
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          Bu seçeneği işaretlerseniz, bu şablonla daha önce email gönderilen kişiler listeden çıkarılır.
                          {previouslySentCount > 0 && (
                            <span className="text-orange-600 font-semibold ml-1">
                              ({previouslySentCount} kişi hariç tutulacak)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Limit Selection */}
                  {selectionMode === 'limit' && (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                      <Label className="font-medium text-[#0f172a] mb-3 block text-lg">
                        📊 Kaç Kişiye Gönderilsin?
                      </Label>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="number"
                          placeholder="Örn: 100, 300, 500"
                          value={recipientLimit}
                          onChange={(e) => setRecipientLimit(e.target.value)}
                          className="flex-1 px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-semibold"
                          min="1"
                          max={recipients.length}
                        />
                        <Button
                          onClick={handleLimitSelection}
                          className="bg-blue-600 hover:bg-blue-700 px-6"
                        >
                          Seç
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600">
                        💡 Toplam <strong>{recipients.length}</strong> alıcı mevcut. 
                        {recipientLimit && ` ${Math.min(parseInt(recipientLimit) || 0, recipients.length)} kişi rastgele seçilecek.`}
                      </p>
                    </div>
                  )}

                  {/* Group Selection */}
                  {selectionMode === 'group' && (
                    <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                      <Label className="font-medium text-[#0f172a] mb-2 block">Grup Seçin:</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {uniqueGroups.map((group) => (
                          <button
                            key={group}
                            onClick={() => handleGroupSelection(group)}
                            className={`px-4 py-3 rounded-lg font-medium text-left transition-colors ${
                              selectedGroup === group
                                ? 'bg-[#6366f1] text-white'
                                : 'bg-white text-[#64748b] hover:bg-[#f1f5f9] border border-[#e2e8f0]'
                            }`}
                          >
                            <div className="font-bold">{group}</div>
                            <div className="text-xs opacity-80">
                              {recipients.filter(r => r.group === group).length} alıcı
                            </div>
                          </button>
                        ))}
                      </div>
                      {selectedGroup && (
                        <p className="text-sm text-[#10b981] mt-3 font-medium">
                          ✓ {selectedRecipients.length} alıcı seçildi ({selectedGroup})
                        </p>
                      )}
                    </div>
                  )}

                  {/* Individual Selection */}
                  {selectionMode === 'individual' && (
                    <>
                      <div className="flex items-center gap-2 p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                        <Checkbox
                          checked={selectedRecipients.length === filteredRecipients.length && filteredRecipients.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-recipients"
                        />
                        <label className="text-sm font-medium text-[#0f172a]">
                          Tümünü Seç ({filteredRecipients.length} alıcı)
                        </label>
                      </div>

                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {filteredRecipients.map((recipient) => (
                      <div
                        key={recipient.id}
                        data-testid={`recipient-checkbox-${recipient.id}`}
                        className="flex items-center gap-3 p-3 hover:bg-[#f8fafc] rounded-lg border border-[#e2e8f0] transition-colors"
                      >
                        <Checkbox
                          checked={selectedRecipients.includes(recipient.id)}
                          onCheckedChange={() => handleRecipientToggle(recipient.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-[#0f172a]">{recipient.email}</p>
                          <p className="text-sm text-[#64748b]">{recipient.name || 'No name'}</p>
                        </div>
                        {recipient.group && (
                          <span className="px-2 py-1 bg-[#6366f1]/10 text-[#6366f1] rounded-md text-xs font-medium">
                            {recipient.group}
                          </span>
                        )}
                        </div>
                      ))}
                    </div>
                  </>
                  )}

                  {/* All mode info */}
                  {selectionMode === 'all' && (
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <p className="text-green-800 font-medium">
                        ✓ Tüm alıcılara gönderilecek ({recipients.length} alıcı)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6 sticky top-8">
            <h3 className="text-lg font-bold text-[#0f172a] mb-6">Kampanya Özeti</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-[#64748b] mb-2">Şablon</p>
                <p className="text-[#0f172a] font-medium">
                  {selectedTemplate
                    ? templates.find(t => t.id === selectedTemplate)?.name
                    : 'Seçilmedi'}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-[#64748b] mb-2">Alıcılar</p>
                <p className="text-[#0f172a] font-medium">
                  {selectedRecipients.length} seçildi
                </p>
              </div>

              {step === 2 && (
                <div className="space-y-4 pt-4 border-t border-[#e2e8f0]">
                  <div>
                    <Label htmlFor="schedule-date" className="text-sm font-medium text-[#64748b] mb-2">Zamanlama (İsteğe Bağlı)</Label>
                    <input
                      id="schedule-date"
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg mt-1"
                    />
                  </div>
                  {scheduleDate && (
                    <div>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {step === 2 && (
              <Button
                onClick={handleSend}
                disabled={loading || !selectedTemplate || selectedRecipients.length === 0}
                data-testid="send-campaign-btn"
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white mt-6 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Kampanya Gönder
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendCampaign;